import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Button, FloatingLabel, Textarea } from 'flowbite-react';
import { sendContactForm } from '@/api/contact';
import classNames from 'classnames';
import { IconLoader2, IconSend } from '@tabler/icons-react';
import toast from 'react-hot-toast';

interface ContactFormProps {
  inputTheme: any;
  isMobileFormShown: boolean;
  onSuccess?: () => void;
}

function ContactForm({ inputTheme, isMobileFormShown, onSuccess }: ContactFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    const formElement = e.currentTarget as HTMLFormElement;
    const formData = new FormData(formElement);

    try {
      await sendContactForm({
        name: formData.get('name') as string,
        email: formData.get('email') as string,
        message: formData.get('message') as string,
        metadata: {
          path: typeof window !== 'undefined' ? window.location.pathname : undefined,
          href: typeof window !== 'undefined' ? window.location.href : undefined,
          userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
        },
      });
      formElement.reset();
      toast.success('Message sent! We will be in touch soon.');
      onSuccess?.();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to send message';
      setError(message);
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className={classNames(
        'bg-white dark:bg-zinc-900 rounded-lg overflow-hidden flex flex-1 flex-col',
        { 'max-sm:hidden': !isMobileFormShown },
      )}
      style={{ borderRadius: 24 }}
    >
      {/* Form Fields */}
      <motion.div
        animate={{ opacity: 1, y: 0 }}
        initial={{ opacity: 0, y: 20 }}
        transition={{ delay: 0.1, duration: 0.5 }}
        className="flex-1 flex flex-col"
      >
        <div className="p-3 inline-flex w-full items-center gap-3 h-16 dark:border-b-1 dark:border-zinc-600 mb-4">
          <div className="text-zinc-500 text-lg">To:</div>
          <span className="bg-zinc-200 text-zinc-600 inline-flex gap-2 items-center text-md font-medium me-2 px-2.5 py-1 rounded-full dark:bg-zinc-700 dark:text-zinc-200">
            <span className="flex w-3 h-3 bg-red-500 rounded-full" />
            Turbo Labs
          </span>
        </div>

        {error && (
          <div className="mx-3 p-4 text-red-500 bg-red-50 dark:bg-red-900/20 rounded-lg">
            {error}
          </div>
        )}

        <FloatingLabel
          className="p-3"
          label="Name"
          style={{ height: 60 }}
          theme={inputTheme}
          variant="standard"
          id="name"
          name="name"
          required
        />
        <FloatingLabel
          className="p-2"
          label="Email"
          style={{ height: 60 }}
          theme={inputTheme}
          variant="standard"
          id="email"
          name="email"
          type="email"
          required
        />
        <Textarea
          className="border-0 flex-1 border-b-2 bg-transparent dark:bg-zinc-900 text-lg p-3 px-2.5 rounded-none focus:border-0 focus:border-b-2 focus:border-zinc-600 focus:ring-0"
          id="message"
          name="message"
          placeholder="Message"
          rows={4}
          required
        />
      </motion.div>

      {/* Submit Button */}
      <div className="p-4 inline-flex items-center gap-7">
        <div className="text-xs dark:text-zinc-500">
          This site is protected by reCAPTCHA and the Google Privacy Policy and
          Terms of Service apply.
        </div>
        <div className="inline-flex items-center gap-3">
          <div className="text-zinc-500 dark:text-zinc-200 text-lg">Send</div>
          <Button
            pill
            type="submit"
            disabled={isSubmitting}
            className="p-0 w-11"
            color="dark"
            aria-busy={isSubmitting}
            aria-live="polite"
          >
            {isSubmitting ? (
              <IconLoader2 className="h-6 w-6 animate-spin" aria-hidden />
            ) : (
              <IconSend className="w-6 h-6" aria-hidden />
            )}
            <span className="sr-only">Send message</span>
          </Button>
        </div>
      </div>
    </form>
  );
}

export default ContactForm;
